/**
 * Helligkeit am Sitz, nur in diesem Browser.
 * Gespeichert wird ausschließlich Hell oder Dunkel — kein Server, keine Secrets.
 * Standard ist Dunkel, bis der Anwalt umschaltet.
 */

export type PortalAppearance = 'dark' | 'light';

export const PORTAL_APPEARANCE_CHANGED = 'arkorbit:portal-appearance';

/** Marker nur an der Arbeitsoberfläche, wenn Hell gewählt ist. */
export const PORTAL_HOME_LIGHT_CLASS = 'portal-home-light';

/** Marker nur an der Hauptleiste, wenn Hell gewählt ist. Nicht an anderen Reiter-Inhalten. */
export const PORTAL_SHELL_LIGHT_CLASS = 'portal-shell-light';

/** Marker nur am ARKO-Inhalt, wenn Hell gewählt ist. */
export const PORTAL_ARKO_LIGHT_CLASS = 'portal-arko-light';

/** Marker nur am Suche-Inhalt, wenn Hell gewählt ist. */
export const PORTAL_SUCHE_LIGHT_CLASS = 'portal-suche-light';

/** Marker nur am Diktat-Inhalt, wenn Hell gewählt ist. */
export const PORTAL_DIKTAT_LIGHT_CLASS = 'portal-diktat-light';

/** Warmer Stein. Kein Weiß, kein kühles Blau-Grau. */
export const PORTAL_HOME_LIGHT = {
  ground: '#e6e1d8',
  raised: '#f3efe8',
  text: '#1c1915',
  muted: '#3f3832',
  accent: '#5b4678',
  edge: '#6a5684',
} as const;

type AppearanceStorage = Pick<Storage, 'getItem' | 'setItem'>;

function portalAppearanceStorageKey(seatId: string): string {
  const safe = String(seatId).replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 80);
  return `arkorbit.portal.appearance.v1.${safe}`;
}

export function loadPortalAppearance(
  storage: Pick<Storage, 'getItem'> | null,
  seatId: string,
): PortalAppearance {
  if (!storage || !seatId.trim()) return 'dark';
  const raw = storage.getItem(portalAppearanceStorageKey(seatId));
  return raw === 'light' ? 'light' : 'dark';
}

function dispatchAppearanceChange(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(PORTAL_APPEARANCE_CHANGED));
}

/** Nur die Wahl. Kein Name, keine Kanzlei, kein weiteres Feld. */
export function savePortalAppearance(
  storage: AppearanceStorage | null,
  seatId: string,
  appearance: PortalAppearance,
): void {
  if (!storage || !seatId.trim()) return;
  const value: PortalAppearance = appearance === 'light' ? 'light' : 'dark';
  storage.setItem(portalAppearanceStorageKey(seatId), value);
  dispatchAppearanceChange();
}

/** Helle Klasse nur für die Arbeitsoberfläche. Dunkel und jede andere Ansicht: leer. */
export function portalHomeSurfaceClass(appearance: PortalAppearance): string {
  return appearance === 'light' ? PORTAL_HOME_LIGHT_CLASS : '';
}

/** Helle Klasse nur für die Hauptleiste. Andere Reiter-Inhalte bleiben ohne sie. */
export function portalShellSurfaceClass(appearance: PortalAppearance): string {
  return appearance === 'light' ? PORTAL_SHELL_LIGHT_CLASS : '';
}

/** Helle Klasse nur für den ARKO-Inhalt. Dunkel und jeder andere Reiter: leer. */
export function portalArkoSurfaceClass(appearance: PortalAppearance): string {
  return appearance === 'light' ? PORTAL_ARKO_LIGHT_CLASS : '';
}

/** Helle Klasse nur für den Suche-Inhalt. Dunkel und jeder andere Reiter: leer. */
export function portalSucheSurfaceClass(appearance: PortalAppearance): string {
  return appearance === 'light' ? PORTAL_SUCHE_LIGHT_CLASS : '';
}

/** Helle Klasse nur für den Diktat-Inhalt. Dunkel und jeder andere Reiter: leer. */
export function portalDiktatSurfaceClass(appearance: PortalAppearance): string {
  return appearance === 'light' ? PORTAL_DIKTAT_LIGHT_CLASS : '';
}
